import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  OnDestroy,
  Output,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  PublicAuthService,
  PublicLoginPayload,
} from '@app/services/auth/public-auth/public-auth.service';
import { PublicToastService } from '@app/services/ui/public-toast/public-toast.service';

@Component({
  selector: 'app-public-auth-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './public-auth-modal.component.html',
  styleUrl: './public-auth-modal.component.scss',
})
export class PublicAuthModalComponent implements OnInit, OnDestroy {
  @Output() closed = new EventEmitter<void>();
  @Input() resetEmail = '';
  @Input() resetToken = '';

  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly publicAuthService = inject(PublicAuthService);
  private readonly publicToastService = inject(PublicToastService);

  protected readonly showPassword = signal(false);
  protected readonly loginSending = signal(false);
  protected readonly loginError = signal('');
  protected readonly resetError = signal('');
  protected readonly resetMode = signal(false);
  protected readonly resetOtpSent = signal(false);
  protected readonly setPasswordMode = signal(false);
  protected readonly resetSending = signal(false);
  protected readonly resendCooldown = signal(0);
  protected readonly activeUser = this.publicAuthService.user;
  protected readonly loginForm = this.initLoginForm();
  protected readonly resetForm = this.initResetForm();
  protected readonly setPasswordForm = this.initSetPasswordForm();
  private resendCooldownTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    this.loginForm.valueChanges.subscribe(() => {
      if (this.loginError()) {
        this.loginError.set('');
      }
    });

    this.resetForm.valueChanges.subscribe(() => {
      if (this.resetError()) {
        this.resetError.set('');
      }
    });
  }

  ngOnInit(): void {
    if (this.resetToken) {
      void this.openSetPasswordFromLink(this.resetEmail, this.resetToken);
    }
  }

  close(): void {
    this.loginError.set('');
    this.resetError.set('');
    this.resetMode.set(false);
    this.resetOtpSent.set(false);
    this.setPasswordMode.set(false);
    this.stopResendCooldown();
    this.closed.emit();
  }

  openSignup(): void {
    this.loginError.set('');
    this.close();
    void this.router.navigate(['/visitor-signup']);
  }

  openForgotPassword(): void {
    this.resetError.set('');
    this.resetMode.set(true);
    this.resetOtpSent.set(false);
    this.setPasswordMode.set(false);
    this.stopResendCooldown();
    this.resetForm.reset({
      email: String(this.loginForm.controls.email.value ?? ''),
      otp: '',
    });
  }

  backToLogin(): void {
    this.resetError.set('');
    this.resetMode.set(false);
    this.setPasswordMode.set(false);
    this.stopResendCooldown();
  }

  togglePasswordVisibility(): void {
    this.showPassword.update((value) => !value);
  }

  async login(): Promise<void> {
    this.loginError.set('');

    if (this.loginForm.invalid || this.loginSending()) {
      return;
    }

    const payload = this.buildLoginPayload();
    this.loginSending.set(true);
    try {
      const accountType = await this.publicAuthService.login(payload);
      this.loginError.set('');
      this.showToast({
        title: accountType === 'admin' ? 'Admin Session Active' : 'Welcome Back',
        message:
          accountType === 'admin'
            ? 'Signed in with your admin account.'
            : 'Signed in successfully.',
        type: 'success',
      });
      this.close();
      void this.router.navigateByUrl(this.publicAuthService.redirectPath);
    } catch {
      this.loginError.set(
        'Invalid email or password. Please try again.',
      );
    } finally {
      this.loginSending.set(false);
    }
  }

  async requestPasswordReset(): Promise<void> {
    this.resetError.set('');

    const emailControl = this.resetForm.controls.email;
    if (emailControl.invalid) {
      this.resetError.set('Enter a valid email address.');
      return;
    }

    this.resetSending.set(true);
    try {
      const message = await this.publicAuthService.requestPasswordReset(
        String(emailControl.value ?? ''),
      );
      this.resetOtpSent.set(true);
      this.resetForm.controls.otp.reset('');
      this.startResendCooldown();
      this.showToast({
        title: 'OTP Sent',
        message,
        type: 'success',
      });
    } catch (error) {
      this.resetError.set(this.errorMessage(error, 'Unable to send reset OTP right now.'));
    } finally {
      this.resetSending.set(false);
    }
  }

  async resendPasswordResetOtp(): Promise<void> {
    if (this.resetSending() || this.resendCooldown() > 0) {
      return;
    }

    await this.requestPasswordReset();
  }

  async verifyPasswordReset(): Promise<void> {
    this.resetError.set('');

    if (this.resetForm.invalid) {
      this.resetError.set('Enter your email and 6-digit OTP.');
      return;
    }

    this.resetSending.set(true);
    try {
      const { email, otp } = this.resetForm.getRawValue();
      const resetToken = await this.publicAuthService.verifyPasswordReset(
        String(email ?? ''),
        String(otp ?? ''),
      );
      this.openSetPassword(String(email ?? ''), resetToken);
    } catch (error) {
      this.resetError.set(this.errorMessage(error, 'Unable to verify reset OTP.'));
    } finally {
      this.resetSending.set(false);
    }
  }

  async completePasswordReset(): Promise<void> {
    this.resetError.set('');

    if (this.setPasswordForm.invalid) {
      this.resetError.set('Enter a valid new password and confirm it.');
      return;
    }

    const { email, reset_token, password, password_confirmation } = this.setPasswordForm.getRawValue();
    if (password !== password_confirmation) {
      this.resetError.set('New password and confirm password must match.');
      return;
    }

    if (!this.publicAuthService.isStrongPassword(String(password ?? ''))) {
      this.resetError.set(
        'Password must be 8-20 characters and include at least 1 uppercase letter, 1 number, and 1 special character.',
      );
      return;
    }

    this.resetSending.set(true);
    try {
      const message = await this.publicAuthService.completePasswordReset(
        String(email ?? ''),
        String(reset_token ?? ''),
        String(password ?? ''),
        String(password_confirmation ?? ''),
      );
      this.showToast({
        title: 'Password Reset',
        message,
        type: 'success',
      });
      this.loginForm.reset({
        email: String(email ?? ''),
        password: '',
      });
      this.backToLogin();
    } catch (error) {
      this.resetError.set(this.errorMessage(error, 'Unable to reset your password.'));
    } finally {
      this.resetSending.set(false);
    }
  }

  protected hasResetPasswordInput(): boolean {
    return this.resetPasswordValue.length > 0;
  }

  protected hasValidResetPasswordLength(): boolean {
    return this.resetPasswordValue.length >= 8 && this.resetPasswordValue.length <= 20;
  }

  protected hasResetPasswordUppercase(): boolean {
    return /[A-Z]/.test(this.resetPasswordValue);
  }

  protected hasResetPasswordNumber(): boolean {
    return /\d/.test(this.resetPasswordValue);
  }

  protected hasResetPasswordSpecialCharacter(): boolean {
    return /[^A-Za-z0-9]/.test(this.resetPasswordValue);
  }

  protected resetPasswordsMatch(): boolean {
    return this.resetPasswordValue.length > 0
      && this.resetPasswordValue === this.resetPasswordConfirmationValue;
  }

  protected resetPasswordMismatch(): boolean {
    return this.resetPasswordValue.length > 0
      && this.resetPasswordConfirmationValue.length > 0
      && !this.resetPasswordsMatch();
  }

  protected canSubmitPasswordReset(): boolean {
    return this.setPasswordForm.valid
      && this.hasValidResetPasswordLength()
      && this.hasResetPasswordUppercase()
      && this.hasResetPasswordNumber()
      && this.hasResetPasswordSpecialCharacter()
      && this.resetPasswordsMatch();
  }

  private get resetPasswordValue(): string {
    return String(this.setPasswordForm.controls.password.value ?? '').trim();
  }

  private get resetPasswordConfirmationValue(): string {
    return String(this.setPasswordForm.controls.password_confirmation.value ?? '').trim();
  }

  logout(): void {
    this.publicAuthService.logout();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  ngOnDestroy(): void {
    this.stopResendCooldown();
  }

  private initLoginForm() {
    return this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.maxLength(60)]],
      password: ['', Validators.required],
    });
  }

  private initResetForm() {
    return this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.maxLength(60)]],
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
  }

  private initSetPasswordForm() {
    return this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.maxLength(60)]],
      reset_token: ['', [Validators.required]],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(20),
        ],
      ],
      password_confirmation: ['', [Validators.required]],
    });
  }

  private openSetPassword(email: string, resetToken: string): void {
    this.resetError.set('');
    this.resetMode.set(true);
    this.resetOtpSent.set(true);
    this.setPasswordMode.set(true);
    this.stopResendCooldown();
    this.setPasswordForm.reset({
      email: email.trim().toLowerCase(),
      reset_token: resetToken,
      password: '',
      password_confirmation: '',
    });
  }

  private async openSetPasswordFromLink(email: string, resetToken: string): Promise<void> {
    this.resetMode.set(true);
    this.resetSending.set(true);
    this.resetError.set('');

    try {
      const verifiedEmail = await this.publicAuthService.verifyPasswordResetLink(email, resetToken);
      this.openSetPassword(verifiedEmail, resetToken);
    } catch (error) {
      this.setPasswordMode.set(false);
      this.resetOtpSent.set(false);
      this.resetForm.patchValue({ email });
      this.resetError.set(
        this.errorMessage(
          error,
          'This password reset link is invalid or expired. Please request a new one.',
        ),
      );
    } finally {
      this.resetSending.set(false);
    }
  }

  private buildLoginPayload(): PublicLoginPayload {
    const { email, password } = this.loginForm.getRawValue();

    return {
      email: String(email ?? '').trim(),
      password: String(password ?? ''),
    };
  }

  private errorMessage(error: unknown, fallback: string): string {
    if (
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      typeof error.status === 'number' &&
      error.status >= 500
    ) {
      return fallback;
    }

    if (
      typeof error === 'object' &&
      error !== null &&
      'error' in error &&
      typeof error.error === 'object' &&
      error.error !== null &&
      'message' in error.error &&
      typeof error.error.message === 'string'
    ) {
      return error.error.message;
    }

    return fallback;
  }

  private showToast(payload: {
    title?: string;
    message: string;
    type?: 'success' | 'error' | 'warning' | 'info';
  }): void {
    this.publicToastService.show({
      title: payload.title,
      message: payload.message,
      type: payload.type ?? 'info',
    });
  }

  private startResendCooldown(): void {
    this.stopResendCooldown();
    this.resendCooldown.set(60);
    this.resendCooldownTimer = setInterval(() => {
      const nextValue = this.resendCooldown() - 1;
      if (nextValue <= 0) {
        this.stopResendCooldown();
        return;
      }

      this.resendCooldown.set(nextValue);
    }, 1000);
  }

  private stopResendCooldown(): void {
    if (this.resendCooldownTimer) {
      clearInterval(this.resendCooldownTimer);
      this.resendCooldownTimer = null;
    }
    this.resendCooldown.set(0);
  }
}
